// src/components/FormSettings/VanityUrlField.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  checkVanitySlugAvailability,
  commitFormVanitySlug,
} from '../../utils/firestore';

const BASE_DOMAIN = 'utmsmartmanager.com';

const RESERVED = new Set([
  'app',
  'localhost',
  '127',
  'www',
]);

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

function normalizeSlug(value) {
  return String(value || '').trim().toLowerCase();
}

function getInvalidHelper(reason) {
  if (reason === 'reserved') {
    return 'This word is reserved.';
  }

  return 'Use lowercase letters, numbers, and hyphens.';
}

function getSaveErrorMessage(error) {
  if (error?.code === 'functions/not-found') {
    return 'Save or publish this form before assigning a vanity URL.';
  }

  if (error?.code === 'functions/already-exists') {
    return 'Unavailable. Try another name.';
  }

  if (error?.code === 'functions/permission-denied') {
    return 'You do not have permission to manage this vanity URL.';
  }

  if (error?.code === 'functions/failed-precondition') {
    return error?.message || 'This vanity URL cannot be assigned.';
  }

  return 'Could not save vanity URL. Try again.';
}

/**
 * Enterprise vanity URL field.
 *
 * This field no longer writes pending state to localStorage and no longer
 * reads or writes vanityUrls/{slug} directly. Availability checks and commits
 * both go through backend callables.
 *
 * UX behavior:
 *  - Existing committed slug loads from initialSlug.
 *  - Search icon checks availability.
 *  - Save icon saves or updates the vanity URL.
 *  - Delete icon removes the committed vanity URL after confirmation.
 *
 * Props:
 *  - formId: string
 *  - isPublished: boolean
 *  - initialSlug?: string|null
 *  - onSaved?: (slug: string|null, result: object) => void
 */
export default function VanityUrlField({
  formId,
  isPublished,
  initialSlug = '',
  onSaved,
}) {
  const [input, setInput] = useState(normalizeSlug(initialSlug));
  const [committedSlug, setCommittedSlug] = useState(
    normalizeSlug(initialSlug)
  );
  const [status, setStatus] = useState('idle');
  const [helper, setHelper] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [busyAction, setBusyAction] = useState(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const normalized = normalizeSlug(initialSlug);

    setInput(normalized);
    setCommittedSlug(normalized);
    setStatus('idle');
    setHelper('');
  }, [initialSlug]);

  const desiredSlug = useMemo(() => normalizeSlug(input), [input]);
  const isBusy = Boolean(busyAction);

  const isDirty = useMemo(
    () => desiredSlug !== committedSlug,
    [desiredSlug, committedSlug]
  );

  const validate = useCallback((slug) => {
    if (!slug) return { ok: true, reason: 'empty' };
    if (!SLUG_RE.test(slug)) return { ok: false, reason: 'format' };
    if (RESERVED.has(slug)) return { ok: false, reason: 'reserved' };

    return { ok: true, reason: 'valid' };
  }, []);

  useEffect(() => {
    const s = desiredSlug;

    if (!s || !SLUG_RE.test(s)) {
      setSuggestions([]);
      return;
    }

    const base = s.replace(/-+\d+$/, '');
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const rand = Math.floor(100 + Math.random() * 900);

    setSuggestions([
      `${base}-${mm}${yy}`,
      `${base}-${yy}`,
      `${base}-${rand}`,
      `${base}-forms`,
    ]);
  }, [desiredSlug]);

  const handleVerify = useCallback(async () => {
    const v = validate(desiredSlug);

    if (!v.ok) {
      setStatus('invalid');
      setHelper(getInvalidHelper(v.reason));
      return;
    }

    if (!desiredSlug) {
      setStatus('idle');
      setHelper(
        committedSlug
          ? 'Enter a new address or use remove to delete the current one.'
          : 'Optional. Leave blank if not needed.'
      );
      return;
    }

    setBusyAction('check');
    setStatus('checking');
    setHelper('');

    console.log('[vanity] checking availability', {
      formId,
      desiredSlug,
    });

    try {
      const result = await checkVanitySlugAvailability(
        desiredSlug,
        formId
      );

      if (!mountedRef.current) return;

      console.log('[vanity] availability result', {
        formId,
        desiredSlug,
        result,
      });

      if (result.state === 'owned') {
        setStatus('owned');
        setHelper('This vanity URL is already assigned to this form.');
        return;
      }

      if (result.available) {
        setStatus('available');
        setHelper('Available. Save to assign this vanity URL.');
        return;
      }

      setStatus('unavailable');
      setHelper('Unavailable. Try another name.');
    } catch (error) {
      if (!mountedRef.current) return;

      console.error('[vanity] availability check failed:', error);
      setStatus('error');
      setHelper('Could not check availability. Try again.');
    } finally {
      if (mountedRef.current) {
        setBusyAction(null);
      }
    }
  }, [
    committedSlug,
    desiredSlug,
    formId,
    validate,
  ]);

  const handleSave = useCallback(async () => {
    const v = validate(desiredSlug);

    if (!formId) {
      setStatus('error');
      setHelper('The form must be ready before saving a vanity URL.');
      return;
    }

    if (!desiredSlug) {
      setStatus('idle');
      setHelper(
        committedSlug
          ? 'Use remove to delete the current vanity URL.'
          : 'Enter a custom address before saving.'
      );
      return;
    }

    if (!v.ok) {
      setStatus('invalid');
      setHelper(getInvalidHelper(v.reason));
      return;
    }

    setBusyAction('save');
    setStatus('saving');
    setHelper('');

    console.log('[vanity] saving vanity URL', {
      formId,
      desiredSlug,
      committedSlug: committedSlug || null,
    });

    try {
      const result = await commitFormVanitySlug(
        formId,
        desiredSlug
      );

      if (!mountedRef.current) return;

      const savedSlug = result.slug || '';

      setCommittedSlug(savedSlug);
      setInput(savedSlug);
      setStatus(savedSlug ? 'saved' : 'cleared');
      setHelper(
        savedSlug
          ? `Saved: https://${savedSlug}.${BASE_DOMAIN}`
          : 'Vanity URL removed.'
      );

      console.log('[vanity] saved vanity URL', {
        formId,
        result,
      });

      onSaved?.(savedSlug || null, result);
    } catch (error) {
      if (!mountedRef.current) return;

      console.error('[vanity] save failed:', error);

      if (error?.code === 'functions/already-exists') {
        setStatus('unavailable');
      } else {
        setStatus('error');
      }

      setHelper(getSaveErrorMessage(error));
    } finally {
      if (mountedRef.current) {
        setBusyAction(null);
      }
    }
  }, [
    committedSlug,
    desiredSlug,
    formId,
    onSaved,
    validate,
  ]);

  const handleOpenRemoveDialog = useCallback(() => {
    if (!committedSlug || isBusy) return;
    setRemoveDialogOpen(true);
  }, [committedSlug, isBusy]);

  const handleCloseRemoveDialog = useCallback(() => {
    if (busyAction === 'remove') return;
    setRemoveDialogOpen(false);
  }, [busyAction]);

  const handleRemove = useCallback(async () => {
    if (!formId || !committedSlug) {
      setRemoveDialogOpen(false);
      return;
    }

    setBusyAction('remove');
    setStatus('clearing');
    setHelper('');

    console.log('[vanity] removing vanity URL', {
      formId,
      committedSlug,
    });

    try {
      const result = await commitFormVanitySlug(formId, null);

      if (!mountedRef.current) return;

      setInput('');
      setCommittedSlug('');
      setStatus('cleared');
      setHelper('Vanity URL removed.');
      setRemoveDialogOpen(false);

      console.log('[vanity] removed vanity URL', {
        formId,
        result,
      });

      onSaved?.(null, result);
    } catch (error) {
      if (!mountedRef.current) return;

      console.error('[vanity] remove failed:', error);
      setStatus('error');
      setHelper(getSaveErrorMessage(error));
    } finally {
      if (mountedRef.current) {
        setBusyAction(null);
      }
    }
  }, [
    committedSlug,
    formId,
    onSaved,
  ]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      if (isDirty && desiredSlug) {
        handleSave();
        return;
      }

      handleVerify();
    }
  }, [
    desiredSlug,
    handleSave,
    handleVerify,
    isDirty,
  ]);

  const computeFieldSx = useCallback((theme) => {
    const successC = theme.palette.success?.main || '#2e7d32';
    const errorC = theme.palette.error?.main || '#d32f2f';
    const greyC = theme.palette.text.disabled;

    let c;

    if (
      status === 'checking' ||
      status === 'saving' ||
      status === 'clearing'
    ) {
      c = greyC;
    } else if (
      status === 'available' ||
      status === 'owned' ||
      status === 'saved' ||
      status === 'cleared'
    ) {
      c = successC;
    } else if (
      status === 'unavailable' ||
      status === 'invalid' ||
      status === 'error'
    ) {
      c = errorC;
    }

    if (!c) return {};

    return {
      '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: c },
        '&:hover fieldset': { borderColor: c },
        '&.Mui-focused fieldset': {
          borderColor: c,
          boxShadow: `0 0 0 1px ${c}`,
        },
      },
      '& .MuiInputLabel-root': {
        color: c,
        '&.Mui-focused': { color: c },
      },
    };
  }, [status]);

  const isErrorState =
    status === 'unavailable' ||
    status === 'invalid' ||
    status === 'error';

  const defaultHelper = useMemo(() => {
    if (isDirty && desiredSlug) {
      return 'Unsaved change. Save to update this vanity URL.';
    }

    if (isDirty && !desiredSlug && committedSlug) {
      return 'Enter a new address or use remove to delete the current one.';
    }

    if (committedSlug) {
      return `Current: https://${committedSlug}.${BASE_DOMAIN}`;
    }

    if (!isPublished) {
      return 'Save or publish this form before assigning a vanity URL.';
    }

    return 'Optional. Leave blank if not needed.';
  }, [
    committedSlug,
    desiredSlug,
    isDirty,
    isPublished,
  ]);

  const canCheck = Boolean(desiredSlug) && !isBusy;
  const canSave =
    Boolean(formId) &&
    Boolean(desiredSlug) &&
    isDirty &&
    !isBusy &&
    status !== 'unavailable';

  const canRemove =
    Boolean(formId) &&
    Boolean(committedSlug) &&
    !isBusy;

  const adornment = (
    <InputAdornment position="end">
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary' }}
      >
        .{BASE_DOMAIN}
      </Typography>
    </InputAdornment>
  );

  const renderActionIcon = (action, icon) => {
    if (busyAction !== action) return icon;

    return (
      <CircularProgress
        size={18}
        thickness={5}
      />
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ width: '100%' }}
        >
          <TextField
            size="medium"
            label="Custom address"
            placeholder="your-brand"
            value={input}
            onChange={(event) => {
              const s = event.target.value.toLowerCase();

              setInput(s);
              setStatus('idle');
              setHelper('');
            }}
            onKeyDown={handleKeyDown}
            InputProps={{ endAdornment: adornment }}
            helperText={helper || defaultHelper}
            error={isErrorState}
            disabled={isBusy}
            fullWidth
            sx={(theme) => computeFieldSx(theme)}
          />

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              pt: '8px',
              flexShrink: 0,
            }}
          >
            <Tooltip title="Check availability">
              <span>
                <IconButton
                  aria-label="Check vanity URL availability"
                  size="small"
                  onClick={handleVerify}
                  disabled={!canCheck}
                >
                  {renderActionIcon(
                    'check',
                    <ManageSearchIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Save vanity URL">
              <span>
                <IconButton
                  aria-label="Save vanity URL"
                  color="primary"
                  size="small"
                  onClick={handleSave}
                  disabled={!canSave}
                >
                  {renderActionIcon(
                    'save',
                    <SaveOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {committedSlug && (
              <Tooltip title="Remove vanity URL">
                <span>
                  <IconButton
                    aria-label="Remove vanity URL"
                    color="error"
                    size="small"
                    onClick={handleOpenRemoveDialog}
                    disabled={!canRemove}
                  >
                    {renderActionIcon(
                      'remove',
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Collapse in={isErrorState && suggestions.length > 0}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              mt: 1,
              flexWrap: 'wrap',
            }}
          >
            {suggestions.map((s) => (
              <ChipLike
                key={s}
                label={`${s}.${BASE_DOMAIN}`}
                onClick={() => {
                  setInput(s);
                  setStatus('idle');
                  setHelper('');
                }}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>

      <Dialog
        open={removeDialogOpen}
        onClose={handleCloseRemoveDialog}
        aria-labelledby="remove-vanity-url-title"
        aria-describedby="remove-vanity-url-description"
      >
        <DialogTitle id="remove-vanity-url-title">
          Remove vanity URL?
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="remove-vanity-url-description">
            This will remove the custom address
            {' '}
            <strong>
              {committedSlug
                ? `${committedSlug}.${BASE_DOMAIN}`
                : ''}
            </strong>
            {' '}
            from this form. The form itself will not be deleted.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseRemoveDialog}
            disabled={busyAction === 'remove'}
          >
            Cancel
          </Button>

          <Button
            color="error"
            onClick={handleRemove}
            disabled={busyAction === 'remove'}
            startIcon={
              busyAction === 'remove'
                ? <CircularProgress size={16} />
                : null
            }
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ChipLike({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: 16,
        padding: '4px 10px',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 12,
        lineHeight: 1.6,
        marginRight: 8,
        marginBottom: 8,
      }}
      type="button"
    >
      {label}
    </button>
  );
}